import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

/**
 * Hook to generate dynamic page title based on current route and Redux state
 *
 * Provides contextual page titles that reflect the user's current location
 * in the application, using entity names from Redux state when available.
 *
 * @returns {string} - Formatted page title
 *
 * @example
 * const pageTitle = useDynamicTitle()
 * // Returns: "Dashboard: Sunday Services" when viewing /dashboards/123
 */
export function useDynamicTitle() {
  const location = useLocation()
  const params = useParams()

  // Fetch data from Redux (using entity adapters)
  const dashboards = useSelector(state => state.dashboards?.entities || {})
  const metrics = useSelector(state => state.metrics?.entities || {})
  const categories = useSelector(state => state.categories?.entities || {})
  const partitionTypes = useSelector(state => state.partitionTypes?.entities || {})
  const reports = useSelector(state => state.reports?.entities || {})

  const title = useMemo(() => {
    const path = location.pathname

    if (path === '/getting-started') return 'Getting started'

    // Dashboard routes
    if (path === '/dashboards') return 'Dashboards'
    if (path.startsWith('/dashboards/') && params.dashboardId) {
      const dashboard = dashboards[params.dashboardId]
      return dashboard ? `Dashboard: ${dashboard.name}` : 'Dashboard'
    }

    // Metrics routes
    if (path === '/metrics') return 'Metrics'
    if (path === '/metrics/new') return 'Create metric'
    if (path.startsWith('/metrics/') && params.id) {
      const metric = metrics[params.id]
      if (path.endsWith('/edit')) {
        return metric ? `Edit metric: ${metric.name}` : 'Edit metric'
      }
      return metric ? `Metric: ${metric.name}` : 'Metric'
    }

    // Categories routes
    if (path === '/categories') return 'Categories'
    if (path.startsWith('/categories/') && params.id) {
      const category = categories[params.id]
      if (path.endsWith('/edit')) {
        return category ? `Edit category: ${category.name}` : 'Edit category'
      }
      return category ? `Category: ${category.name}` : 'Category'
    }

    // Partition types routes
    if (path === '/partitions') return 'Partition types'
    if (path.startsWith('/partitions/') && params.id) {
      const partitionType = partitionTypes[params.id]
      return partitionType ? `Partition type: ${partitionType.name}` : 'Partition type'
    }

    // Reports routes
    if (path === '/reports') return 'Reports'
    if (path === '/reports/new') return 'Create report'
    if (path.startsWith('/reports/')) {
      const report = reports[params.reportId || params.id || path.split('/')[2]]
      if (path.endsWith('/view')) {
        return report ? `Report: ${report.name || 'Untitled'}` : 'Report'
      }
      if (path.endsWith('/edit')) {
        return report ? `Edit report: ${report.name || 'Untitled'}` : 'Edit report'
      }
      return report ? `Report: ${report.name || 'Untitled'}` : 'Report'
    }

    // Settings routes
    if (path.startsWith('/settings/')) {
      const settingsTab = path.split('/settings/')[1].split('/')[0]
      const tabNames = {
        organization: 'Organization',
        branding: 'Branding & appearance',
        authentication: 'Authentication',
        integrations: 'Integrations',
        measurements: 'Measurements',
        partitions: 'Partition types',
        'chart-colors': 'Chart colors',
        users: 'Users & roles',
        campuses: 'Campuses',
        categories: 'Categories',
        subscription: 'Subscription & billing'
      }
      return `Settings: ${tabNames[settingsTab] || 'Settings'}`
    }
    if (path === '/settings') return 'Settings'

    // Data entry route
    if (path === '/data-entry') return 'Data entry'
    if (path === '/data-entry/new') return 'Create form'
    if (path.startsWith('/data-entry/')) return path.endsWith('/edit') ? 'Edit form' : 'Fill form'
    if (path === '/report-templates') return 'PDF templates'
    if (path.startsWith('/report-templates/')) return path.endsWith('/new') ? 'Create PDF template' : 'Edit PDF template'

    // Profile route
    if (path === '/profile') return 'My profile'

    // Default fallback
    return 'Dashboard'
  }, [location.pathname, params, dashboards, metrics, categories, partitionTypes, reports])

  return title
}
